/**
 * ClaimOnboardPage — paste a URL, watch the platform scrape it in real time,
 * promote to Platinum with one tap.
 *
 * This is the Thursday investor demo magic moment: "watch us onboard a venue
 * in 30 seconds."
 *
 * Flow:
 *   1. User types a URL (we pre-fill lashedbykate.com.au for the demo).
 *   2. We hit POST /api/directory/admin/claim-onboard/preview — scrape-only.
 *   3. Render the extracted profile with confidence checkmarks per field.
 *   4. User taps "Promote to Platinum" — we POST /promote and open the new
 *      mini-site in a fresh tab.
 */
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Globe,
  Sparkles,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  CalendarClock,
  Instagram,
  Facebook,
  ExternalLink,
  Film,
  Terminal,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const PROVIDER_LABEL = {
  timely: "Timely",
  fresha: "Fresha",
  ovatu: "Ovatu",
  square_appointments: "Square Appointments",
  calendly: "Calendly",
  setmore: "Setmore",
  booksy: "Booksy",
  simplybook: "SimplyBook.me",
  acuity: "Acuity Scheduling",
  opentable: "OpenTable",
  now_book_it: "Now Book It",
};

function Row({ icon: Icon, label, value, ok, testid }) {
  return (
    <div
      className="flex items-start gap-3 py-2.5 border-b border-stone-100 last:border-b-0"
      data-testid={testid}
    >
      <Icon className="w-4 h-4 mt-1 text-stone-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider font-bold text-stone-500">{label}</div>
        <div className="text-[14px] text-stone-800 break-words">
          {value ? value : <span className="text-stone-300 italic">not found</span>}
        </div>
      </div>
      {ok ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
      ) : (
        <XCircle className="w-4 h-4 text-stone-300 shrink-0" />
      )}
    </div>
  );
}

export default function ClaimOnboardPage() {
  const [url, setUrl] = useState("https://lashedbykate.com.au/");
  const [scraping, setScraping] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [cinematic, setCinematic] = useState(true);
  const [liveLog, setLiveLog] = useState([]);   // {ts, text, status}

  // Fast-track prefill from /platform Savings Calculator submission
  const location = useLocation();
  const [fastTrack, setFastTrack] = useState(null);
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    if (qs.get("from") === "savings-calc") {
      setFastTrack({
        first: qs.get("first") || "",
        last:  qs.get("last")  || "",
        email: qs.get("email") || "",
        mobile: qs.get("mobile") || "",
        biz:   qs.get("biz")   || "",
        postcode: qs.get("postcode") || "",
        saving: qs.get("saving") || "",
      });
    }
  }, [location.search]);

  // ── Cinematic step generator ────────────────────────────────────────
  // The frontend animates a terminal-style log while the real scrape runs
  // in parallel. Steps interpolate the *actual* scraped data once it lands.
  const buildSteps = (targetUrl, prof) => {
    const host = (() => { try { return new URL(targetUrl).hostname; } catch { return targetUrl; } })();
    const provLabel = prof?.booking_provider
      ? (PROVIDER_LABEL[prof.booking_provider] || prof.booking_provider)
      : null;
    return [
      { d: 250,  t: `$ claim-onboard ${host}`,                                                  s: "cmd" },
      { d: 400,  t: `  → resolving DNS for ${host}…`,                                            s: "info" },
      { d: 600,  t: `  ✓ DNS resolved`,                                                           s: "ok" },
      { d: 500,  t: `  → fetching homepage…`,                                                    s: "info" },
      { d: 800,  t: `  ✓ 200 OK · parsing HTML`,                                                  s: "ok" },
      { d: 450,  t: `  → scanning <meta> tags…`,                                                  s: "info" },
      { d: 600,  t: `  ✓ og:title  : "${prof?.name || "?"}"`,                                     s: "ok" },
      { d: 350,  t: `  ✓ og:image  : ${prof?.hero_image_url ? prof.hero_image_url.slice(0, 64) + "…" : "(none)"}`, s: prof?.hero_image_url ? "ok" : "warn" },
      { d: 500,  t: `  → sniffing booking-provider signatures…`,                                  s: "info" },
      { d: 800,  t: provLabel
                      ? `  ✓ matched ${provLabel.toUpperCase()}  →  ${(prof?.external_booking_url || "").slice(0, 64)}…`
                      : `  ⚠ no booking provider detected`,                                       s: provLabel ? "ok" : "warn" },
      { d: 350,  t: `  → extracting social links…`,                                                s: "info" },
      { d: 500,  t: `  ✓ instagram${prof?.instagram ? " ✓" : " ✗"} · facebook${prof?.facebook ? " ✓" : " ✗"}`, s: "ok" },
      { d: 350,  t: `  → geocoding via OSM Nominatim…`,                                            s: "info" },
      { d: 1000, t: prof?.lat
                      ? `  ✓ ${prof.lat.toFixed(4)}, ${prof.lng.toFixed(4)}  ·  ${prof.suburb || ""} ${prof.postcode || ""}`
                      : `  ⚠ no geocode (manual entry required)`,                                  s: prof?.lat ? "ok" : "warn" },
      { d: 400,  t: `  → classifying niche…`,                                                     s: "info" },
      { d: 500,  t: `  ✓ ${prof?.niche || "Tourism"} / ${prof?.sub_niche || "Lifestyle"}`,        s: "ok" },
      { d: 600,  t: `  ✓ profile ready  ·  awaiting promote command`,                              s: "ok" },
    ];
  };

  // Replay the steps with delays
  const playSteps = async (steps) => {
    for (const step of steps) {
      await new Promise((r) => setTimeout(r, step.d));
      setLiveLog((prev) => [...prev, step]);
    }
  };

  // ── Promote replay (for the closing wow)
  const buildPromoteSteps = (slug, id) => [
    { d: 300, t: `$ promote ${slug || "<entity>"} --tier=platinum`,                  s: "cmd" },
    { d: 450, t: `  → reserving entity id…`,                                          s: "info" },
    { d: 500, t: `  ✓ assigned id ${id || "?"}`,                                       s: "ok" },
    { d: 350, t: `  → writing entity document to MongoDB…`,                            s: "info" },
    { d: 500, t: `  ✓ tier flipped to platinum`,                                       s: "ok" },
    { d: 350, t: `  → invalidating directory cache…`,                                  s: "info" },
    { d: 400, t: `  ✓ niche aggregations refreshed`,                                   s: "ok" },
    { d: 500, t: `  → publishing mini-site at /r/${slug || "<slug>"}…`,                s: "info" },
    { d: 600, t: `  ✓ live  ·  10 paid pages enabled  ·  ready to demo`,               s: "ok" },
  ];

  const scrape = async () => {
    setScraping(true);
    setError(null);
    setProfile(null);
    setResult(null);
    setLiveLog([]);
    try {
      // Kick off the real scrape in parallel with the cinematic log
      const scrapePromise = fetch(`${API}/api/directory/admin/claim-onboard/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      }).then((r) => r.json());

      if (cinematic) {
        // Pre-play the first three lines instantly so the user sees motion
        // while the real scrape runs. These exactly mirror buildSteps[0..2]
        // so the slice below resumes without duplication.
        const intro = [
          { d: 250, t: `$ claim-onboard ${(() => { try { return new URL(url).hostname; } catch { return url; } })()}`, s: "cmd" },
          { d: 400, t: `  → resolving DNS for ${(() => { try { return new URL(url).hostname; } catch { return url; } })()}…`, s: "info" },
          { d: 600, t: `  ✓ DNS resolved`, s: "ok" },
        ];
        await playSteps(intro);
      }

      const d = await scrapePromise;
      if (!d.ok) {
        setError(d.error || "Scrape failed");
        return;
      }
      setProfile(d);
      if (cinematic) {
        // Now play the data-aware steps
        const steps = buildSteps(url, d);
        // Skip the first 3 (already shown in intro)
        await playSteps(steps.slice(3));
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setScraping(false);
    }
  };

  const promote = async () => {
    if (!profile) return;
    setPromoting(true);
    setError(null);
    try {
      const promotePromise = fetch(`${API}/api/directory/admin/claim-onboard/promote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profile }),
      }).then((r) => r.json());

      if (cinematic) {
        await playSteps([{ d: 200, t: ``, s: "blank" }]);
      }

      const d = await promotePromise;
      if (!d.ok) {
        setError(d.error || "Promote failed");
        return;
      }
      setResult(d);
      if (cinematic) {
        await playSteps(buildPromoteSteps(d.slug, d.id));
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="claim-onboard-page">
      <header className="bg-gradient-to-r from-stone-900 to-stone-800 text-white">
        <div className="max-w-5xl mx-auto px-6 py-8 sm:py-12">
          <Link to="/" className="text-[12px] text-stone-400 hover:text-white">← HustlePlate</Link>
          <div className="flex items-center gap-2 mt-4 mb-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-amber-400">Investor Demo · Live</div>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight">Claim &amp; Onboard a Venue</h1>
          <p className="text-stone-300 mt-3 max-w-2xl text-[15px] sm:text-[16px]">
            Paste any business website. The platform auto-scrapes the name, hero image,
            booking system (Timely / Fresha / Ovatu / Square / Calendly), phone,
            address, geolocation, and socials &mdash; then promotes the listing to
            <span className="text-amber-400 font-bold"> Platinum</span> with one tap.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Fast-track banner for /platform Savings Calculator high-value leads */}
        {fastTrack && (
          <div className="bg-gradient-to-r from-emerald-700 to-emerald-900 text-white rounded-2xl p-6 shadow-lg" data-testid="claim-onboard-fasttrack">
            <div className="flex items-start gap-4">
              <Sparkles className="w-6 h-6 text-amber-300 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-emerald-200 mb-1">
                  Fast-track unlocked · from your Savings Report
                </div>
                <div className="text-xl sm:text-2xl font-bold mb-1" data-testid="claim-onboard-fasttrack-greet">
                  G'day {fastTrack.first || "there"} — let's onboard {fastTrack.biz || "your business"} live.
                </div>
                <div className="text-emerald-100 text-[14px]">
                  {fastTrack.saving && (<>You'd save <strong className="text-amber-300">${Number(fastTrack.saving).toLocaleString()}/mo</strong>. </>)}
                  Paste your website below and watch us scrape, geocode and publish your mini-site in 30 seconds.
                </div>
                <div className="text-[12px] text-emerald-200/80 mt-3">
                  {fastTrack.email}{fastTrack.mobile ? ` · ${fastTrack.mobile}` : ""}{fastTrack.postcode ? ` · ${fastTrack.postcode}` : ""}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* URL input */}
        <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-[11px] uppercase tracking-wider font-bold text-stone-500">
              Business website URL
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="cinematic-toggle">
              <input
                type="checkbox"
                checked={cinematic}
                onChange={(e) => setCinematic(e.target.checked)}
                className="rounded accent-amber-600"
              />
              <span className="text-[11px] font-bold text-stone-700 inline-flex items-center gap-1">
                <Film className="w-3 h-3" /> Cinematic mode
              </span>
            </label>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com.au"
                data-testid="claim-onboard-url-input"
                className="w-full pl-10 pr-3 py-3 rounded-xl border border-stone-200 focus:border-stone-500 focus:outline-none text-[15px]"
              />
            </div>
            <button
              onClick={scrape}
              disabled={scraping || !url}
              data-testid="claim-onboard-scrape-btn"
              className="px-6 py-3 rounded-xl bg-stone-900 text-white font-bold text-[14px] hover:bg-stone-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {scraping ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Scraping…
                </>
              ) : (
                <>
                  Scrape it <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
            <span className="text-stone-400">Try:</span>
            {[
              "https://lashedbykate.com.au/",
              "https://www.thecoffeecommune.com.au/",
              "https://www.unitedcareqld.com.au/",
            ].map((u) => (
              <button
                key={u}
                onClick={() => setUrl(u)}
                className="text-stone-600 hover:text-stone-900 underline"
                data-testid={`claim-onboard-preset-${new URL(u).hostname}`}
              >
                {new URL(u).hostname}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-[14px]" data-testid="claim-onboard-error">
            {error}
          </div>
        )}

        {/* Cinematic terminal — shows the live log of scrape + promote actions */}
        {cinematic && liveLog.length > 0 && (
          <div
            className="rounded-2xl overflow-hidden border border-stone-800 shadow-xl bg-stone-950"
            data-testid="claim-onboard-terminal"
          >
            <div className="flex items-center gap-2 px-4 py-2 bg-stone-900 border-b border-stone-800">
              <div className="flex gap-1.5">
                <span className="w-3 h-3 rounded-full bg-red-500" />
                <span className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="w-3 h-3 rounded-full bg-emerald-500" />
              </div>
              <Terminal className="w-3.5 h-3.5 text-stone-400 ml-2" />
              <span className="text-[12px] text-stone-300 font-mono">hustleplate@onboard:~$</span>
              <span className="ml-auto text-[10px] text-stone-500 uppercase tracking-wider">LIVE</span>
            </div>
            <div className="px-5 py-4 font-mono text-[13px] leading-[1.65] max-h-96 overflow-y-auto">
              {liveLog.map((line, i) => (
                <div
                  key={i}
                  className={
                    line.s === "cmd" ? "text-amber-400 font-bold mt-2 first:mt-0"
                    : line.s === "ok" ? "text-emerald-400"
                    : line.s === "warn" ? "text-amber-300"
                    : line.s === "blank" ? "" : "text-stone-300"
                  }
                  data-testid={`terminal-line-${i}`}
                >
                  {line.t || "\u00A0"}
                </div>
              ))}
              {(scraping || promoting) && (
                <div className="text-emerald-400 inline-flex items-center gap-1 mt-1">
                  <span className="animate-pulse">▋</span>
                </div>
              )}
            </div>
          </div>
        )}

        {profile && (
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden" data-testid="claim-onboard-profile">
            {/* Header strip */}
            <div className="relative h-44 bg-stone-100">
              {profile.hero_image_url ? (
                <img
                  src={profile.hero_image_url}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-stone-400">No hero image found</div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-3 left-5 right-5 text-white">
                <div className="text-[11px] uppercase tracking-wider opacity-80">{profile.niche} · {profile.sub_niche}</div>
                <div className="text-2xl font-bold">{profile.name}</div>
              </div>
            </div>

            {/* Field grid */}
            <div className="p-5 grid sm:grid-cols-2 gap-x-8">
              <Row icon={Globe}         label="Source URL"      value={profile.url}                  ok={!!profile.url}                  testid="row-url" />
              <Row icon={CalendarClock} label="Booking system"  value={profile.booking_provider ? `${PROVIDER_LABEL[profile.booking_provider] || profile.booking_provider} — ${profile.external_booking_url}` : null} ok={!!profile.booking_provider} testid="row-booking" />
              <Row icon={Phone}         label="Phone"           value={profile.phone}                ok={!!profile.phone}                testid="row-phone" />
              <Row icon={Mail}          label="Email"           value={profile.email}                ok={!!profile.email}                testid="row-email" />
              <Row icon={MapPin}        label="Address"         value={profile.address}              ok={!!profile.address}              testid="row-address" />
              <Row icon={MapPin}        label="Lat / Lng"       value={profile.lat ? `${profile.lat.toFixed(4)}, ${profile.lng?.toFixed(4)}` : null} ok={!!profile.lat} testid="row-latlng" />
              <Row icon={Instagram}     label="Instagram"       value={profile.instagram}            ok={!!profile.instagram}            testid="row-ig" />
              <Row icon={Facebook}      label="Facebook"        value={profile.facebook}             ok={!!profile.facebook}             testid="row-fb" />
            </div>

            {/* Promote bar */}
            <div className="px-5 py-4 bg-stone-50 border-t border-stone-200 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="text-[13px] text-stone-700 flex-1">
                Scraped in {scraping ? "—" : "<2s"}. Ready to promote{" "}
                <span className="font-bold">{profile.name}</span> to{" "}
                <span className="text-amber-700 font-bold">Platinum</span>.
              </div>
              <button
                onClick={promote}
                disabled={promoting}
                data-testid="claim-onboard-promote-btn"
                className="px-5 py-2.5 rounded-full bg-amber-600 text-white font-bold text-[14px] hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {promoting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Promoting…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" /> Promote to Platinum
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5" data-testid="claim-onboard-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
              <div className="flex-1">
                <div className="font-bold text-emerald-900 text-[16px]">{profile.name} is live!</div>
                <div className="text-[13px] text-emerald-800 mt-1">
                  Entity id <span className="font-mono">{result.id}</span> · slug{" "}
                  <span className="font-mono">{result.slug}</span> · tier{" "}
                  <span className="font-bold">{result.tier}</span>.
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="claim-onboard-open-site"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-stone-900 text-white text-[13px] font-bold hover:bg-stone-700"
                  >
                    Open mini-site <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                  <Link
                    to={`/directory?q=${encodeURIComponent(profile.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-stone-300 text-stone-800 text-[13px] font-bold hover:bg-stone-50"
                  >
                    See it in directory
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* How it works strip */}
        <div className="bg-white rounded-2xl border border-stone-200 p-5 mt-8">
          <div className="text-[11px] uppercase tracking-wider font-bold text-stone-500 mb-3">How it works</div>
          <ol className="space-y-2 text-[13px] text-stone-700 list-decimal pl-5">
            <li><span className="font-bold">Open Graph &amp; meta scrape</span> — name, hero, description, social links</li>
            <li><span className="font-bold">Booking provider sniffing</span> — Timely, Fresha, Ovatu, Square, Calendly &amp; more</li>
            <li><span className="font-bold">OSM Nominatim geocode</span> — lat/lng + canonical address</li>
            <li><span className="font-bold">Niche auto-classifier</span> — assigns Tourism/Medical/Liquor/Restaurants + sub-niche</li>
            <li><span className="font-bold">Platinum upsert</span> — tier flipped, mini-site live at <code>/r/&lt;slug&gt;</code></li>
          </ol>
        </div>
      </main>
    </div>
  );
}
